import { useCallback } from 'react';
import { arrayMoveImmutable } from 'array-move';
import { API_URL } from '../Config';

export function useTimelineActions(projectId, timelineItems, setTimelineItems, updateTimelineOrder, currentVideoIndex = 0, videos = []) {

  const addFrame = useCallback(async (timestamp, screenshot) => {
    const newFrameData = {
      type: 'frame',
      timestamp: timestamp,
      rawText: '',
      screenshot: screenshot,
    };
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/timeline-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newFrameData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const createdItem = await response.json();

      setTimelineItems((prevItems) => {
        const outroIndex = prevItems.findIndex(item => item.type === 'outro');
        if (outroIndex !== -1) {
          const newItems = [...prevItems];
          newItems.splice(outroIndex, 0, createdItem);
          updateTimelineOrder(newItems); // Update order in backend
          return newItems;
        } else {
          updateTimelineOrder([...prevItems, createdItem]); // Update order in backend
          return [...prevItems, createdItem];
        }
      });
    } catch (error) {
      console.error("Error adding frame to timeline:", error);
    }
  }, [projectId, setTimelineItems, updateTimelineOrder]);

  const addClip = useCallback(async (startTime, endTime, screenshot) => {
    const newClipData = {
      type: 'clip',
      startTime: startTime,
      endTime: endTime,
      rawText: '',
      screenshot: screenshot,
      // Store which video this clip was created from
      video_index: currentVideoIndex,
      video_url: videos[currentVideoIndex] || null,
    };

    console.log('🎬 Adding clip with data:', {
      ...newClipData,
      screenshot: '[base64 data]', // Don't log the full base64
      currentVideoIndex,
      videosLength: videos.length,
      videos: videos.map(v => v.substring(0, 50) + '...' || 'null')
    });

    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/timeline-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newClipData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const createdItem = await response.json();

      console.log('✅ Clip created in database:', {
        id: createdItem.id,
        type: createdItem.type,
        video_index: createdItem.video_index,
        startTime: createdItem.startTime,
        endTime: createdItem.endTime
      });

      setTimelineItems((prevItems) => {
        const outroIndex = prevItems.findIndex(item => item.type === 'outro');
        if (outroIndex !== -1) {
          const newItems = [...prevItems];
          newItems.splice(outroIndex, 0, createdItem);
          updateTimelineOrder(newItems); // Update order in backend
          return newItems;
        } else {
          updateTimelineOrder([...prevItems, createdItem]); // Update order in backend
          return [...prevItems, createdItem];
        }
      });
    } catch (error) {
      console.error("Error adding clip to timeline:", error);
    }
  }, [projectId, setTimelineItems, updateTimelineOrder, currentVideoIndex, videos]);

  const handleRemoveItem = useCallback(async (id) => {
    try {
      const response = await fetch(`${API_URL}/timeline-items/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setTimelineItems((prevItems) => prevItems.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error removing timeline item:", error);
    }
  }, [setTimelineItems]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = timelineItems.findIndex((item) => item.id === active.id);
      const newIndex = timelineItems.findIndex((item) => item.id === over.id);
      const updatedTimeline = arrayMoveImmutable(timelineItems, oldIndex, newIndex);
      setTimelineItems(updatedTimeline);
      updateTimelineOrder(updatedTimeline);
    }
  }, [timelineItems, setTimelineItems, updateTimelineOrder]);

  const handleMoveUp = (id) => {
    const index = timelineItems.findIndex((item) => item.id === id);
    if (index > 0) {
      const newTimelineItems = arrayMoveImmutable(timelineItems, index, index - 1);
      setTimelineItems(newTimelineItems);
      updateTimelineOrder(newTimelineItems);
    }
  };

  const handleMoveDown = (id) => {
    const index = timelineItems.findIndex((item) => item.id === id);
    if (index < timelineItems.length - 1) {
      const newTimelineItems = arrayMoveImmutable(timelineItems, index, index + 1);
      setTimelineItems(newTimelineItems);
      updateTimelineOrder(newTimelineItems);
    }
  };

  const handleDuplicateItem = useCallback(async (id) => {
    try {
      const response = await fetch(`${API_URL}/timeline-items/${id}/duplicate`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const duplicatedItem = await response.json();
      
      setTimelineItems((prevItems) => {
        const originalIndex = prevItems.findIndex((item) => item.id === id);
        const newItems = [...prevItems];
        newItems.splice(originalIndex + 1, 0, duplicatedItem);
        updateTimelineOrder(newItems); // Update order in backend
        return newItems;
      });
    } catch (error) {
      console.error("Error duplicating timeline item:", error);
    }
  }, [projectId, setTimelineItems, updateTimelineOrder]);

  return { addFrame, addClip, handleRemoveItem, handleDragEnd, handleMoveUp, handleMoveDown, handleDuplicateItem };
}